import { ApiProvider } from '@/provider/ApiProvider';

/**
 * Calls of the notification centre (pnet-docs notifications/Notification-PubSub-Design.md,
 * "API contract", person-facing part) on the notification service at VITE_NOTIFICATION_API_URL, through
 * ApiProvider.serviceCall: the user's token and X-PNET-Expected-Assignment like every
 * identity call. Every answer comes back in the house shape (`{ success: true, data }`, or
 * `{ success: false, status }`), so useAccessQuery and the shared states read it as they read
 * the gateway's. The server's messages are never shown (minimal disclosure).
 */

export type NotificationView = 'all' | 'action' | 'unread';
export type Channel = 'app' | 'email' | 'both' | 'off';
export type EmailTiming = 'now' | 'daily';
/** The state of a message: updated in place by its publisher (e.g. an upload's progress). */
export type NotificationState = 'info' | 'in_progress' | 'done' | 'failed';
/** `managed`: only the owning service subscribes people; `open`: anyone may join. */
export type TopicMode = 'managed' | 'open';

export interface NotificationAction {
  label: string;
  href: string;
}

export interface NotificationItem {
  id: string | number;
  area: string;
  topic: string;
  title: string;
  summary: string;
  needs_action: boolean;
  state: NotificationState;
  /** 0-100 while `in_progress` (null = not known: an indeterminate bar) */
  progress: number | null;
  action: NotificationAction | null;
  created_at: string;
  /** the last update in place (later than `created_at` once updated) */
  updated_at: string | null;
  read_at: string | null;
}

export interface NotificationSummary {
  unread: number;
  needs_action: number;
}

export interface NotificationPage {
  items: NotificationItem[];
  /** the cursor of the next page (`before=`), or null on the last page */
  next: string | null;
}

/** A topic of `GET me/subscriptions`: one the person is subscribed to, or an open one. */
export interface SubscriptionTopic {
  key: string;
  display_name: string;
  description: string;
  area: string;
  mode: TopicMode;
  /** a mandatory subscription is never Off */
  mandatory: boolean;
  subscribed: boolean;
  channel: Channel;
  /** the channels the person may choose */
  choices: Channel[];
}

export interface Subscriptions {
  topics: SubscriptionTopic[];
  email_timing: EmailTiming;
  daily_at: string;
}

/** The body of `PUT me/subscriptions`. */
export interface SubscriptionsInput {
  topics: Record<string, Channel>;
  email_timing: EmailTiming;
  daily_at: string;
}

/** An answer in the house shape (AccessState/accessAnswer.ts reads `success` and `status`). */
export interface ServiceAnswer<T> {
  success: boolean;
  data?: T;
  /** the HTTP status of a failed call; 0 = no answer */
  status?: number;
  reason?: string;
}

/** The service's base URL; '' = no notification backend (read at call time). */
export const notificationApiUrl = (): string =>
  String(import.meta.env.VITE_NOTIFICATION_API_URL ?? '').trim();

export const NOTIFICATION_API_NOT_CONFIGURED = 'notification_api_not_configured';

const query = (q: Record<string, string | number | null | undefined>): string => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q))
    if (v !== null && v !== undefined && String(v).trim() !== '') params.set(k, String(v).trim());
  const s = params.toString();
  return s ? `?${s}` : '';
};

/** A body the service wrapped as `{ success, data }`, or the bare body of the contract. */
const dataOf = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
};

export async function notificationRequest<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<ServiceAnswer<T>> {
  const base = notificationApiUrl();
  if (!base) return { success: false, status: 0, reason: NOTIFICATION_API_NOT_CONFIGURED };
  const { httpStatus, payload } = await ApiProvider.serviceCall(base, method, path, body, signal);
  const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  // The service's PHP envelope answers some failures with HTTP 200 and `{ success: false,
  // code }` (e.g. a route it does not have yet: `code: 404`): a failure with that status.
  const envelopeFailed = p.success === false;
  if (httpStatus >= 200 && httpStatus < 300 && !envelopeFailed) {
    return { success: true, data: (httpStatus === 204 ? null : dataOf(payload)) as T };
  }
  const status = envelopeFailed
    ? Number(p.code) || Number(p.status) || httpStatus || 0
    : httpStatus || Number(p.status) || 0;
  return {
    success: false,
    status: Number.isFinite(status) ? status : 0,
    reason: typeof p.reason === 'string' ? p.reason : undefined,
  };
}

const count = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/** GET me/notifications/summary: the bell. */
export async function getNotificationSummary(
  signal?: AbortSignal,
): Promise<ServiceAnswer<NotificationSummary>> {
  const r = await notificationRequest<Partial<NotificationSummary>>(
    'GET',
    'me/notifications/summary',
    undefined,
    signal,
  );
  if (!r.success) return r as ServiceAnswer<NotificationSummary>;
  return {
    success: true,
    data: { unread: count(r.data?.unread), needs_action: count(r.data?.needs_action) },
  };
}

export interface NotificationListQuery {
  view: NotificationView;
  area: string | null;
  before?: string | null;
  limit: number;
}

const STATES: NotificationState[] = ['info', 'in_progress', 'done', 'failed'];

/** 0-100 (rounded), or null when the service gives none. */
const progressOf = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : null;
};

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const strOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const actionOf = (value: unknown): NotificationAction | null => {
  if (!value || typeof value !== 'object') return null;
  const a = value as Record<string, unknown>;
  return { label: str(a.label), href: str(a.href) };
};

/**
 * An item as the UI reads it: only the fields of the contract's `Notification` (anything else
 * the service may send, such as attributes or a dedupe key, is dropped: notifications are
 * business information and their ids are never shown), a known state (else `info`), progress
 * 0-100 or null.
 */
export function normaliseNotification(raw: Record<string, unknown>): NotificationItem {
  const state = STATES.includes(raw.state as NotificationState)
    ? (raw.state as NotificationState)
    : 'info';
  return {
    id: typeof raw.id === 'number' ? raw.id : str(raw.id),
    area: str(raw.area),
    topic: str(raw.topic),
    title: str(raw.title),
    summary: str(raw.summary),
    needs_action: raw.needs_action === true,
    state,
    progress: state === 'in_progress' ? progressOf(raw.progress) : null,
    action: actionOf(raw.action),
    created_at: str(raw.created_at),
    updated_at: strOrNull(raw.updated_at),
    read_at: strOrNull(raw.read_at),
  };
}

/** GET me/notifications?view=&area=&before=&limit= */
export async function listNotifications(
  q: NotificationListQuery,
  signal?: AbortSignal,
): Promise<ServiceAnswer<NotificationPage>> {
  const r = await notificationRequest<{ items?: unknown; next?: unknown }>(
    'GET',
    `me/notifications${query({ view: q.view, area: q.area, before: q.before, limit: q.limit })}`,
    undefined,
    signal,
  );
  if (!r.success) return r as ServiceAnswer<NotificationPage>;
  const raw: unknown[] = Array.isArray(r.data?.items) ? r.data.items : [];
  const items = raw
    .filter((i): i is Record<string, unknown> => Boolean(i) && typeof i === 'object')
    .map(normaliseNotification);
  const next = typeof r.data?.next === 'string' && r.data.next !== '' ? r.data.next : null;
  return { success: true, data: { items, next } };
}

/** POST me/notifications/{id}/read */
export const markNotificationRead = (id: string | number): Promise<ServiceAnswer<null>> =>
  notificationRequest('POST', `me/notifications/${encodeURIComponent(String(id))}/read`);

/** POST me/notifications/read-all */
export const markAllNotificationsRead = (): Promise<ServiceAnswer<null>> =>
  notificationRequest('POST', 'me/notifications/read-all');

/** GET me/subscriptions: the person's subscriptions, the open topics and the e-mail timing. */
export const getSubscriptions = (signal?: AbortSignal): Promise<ServiceAnswer<Subscriptions>> =>
  notificationRequest('GET', 'me/subscriptions', undefined, signal);

/** PUT me/subscriptions: answers the saved subscriptions (the shape of the GET). */
export const saveSubscriptions = (
  input: SubscriptionsInput,
): Promise<ServiceAnswer<Subscriptions>> =>
  notificationRequest('PUT', 'me/subscriptions', {
    topics: input.topics,
    email_timing: input.email_timing,
    daily_at: input.daily_at,
  });
